import React from 'react';
import { Control, FieldValues, Path } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { BookMarked, Loader2 } from 'lucide-react';
import { Select, SelectOption } from '../ui/Select';
import { learningPathApiService } from '@/services/api/learning-path-api.service';

interface LearningPath {
  _id: string;
  title: string;
}

interface LearningPathMultiSelectProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  label?: string;
  error?: string;
  required?: boolean;
  helperText?: string;
  placeholder?: string;
  disabled?: boolean;
}

export function LearningPathMultiSelect<T extends FieldValues>({
  name,
  control,
  label = 'Learning Paths (Optional)',
  error,
  required = false,
  helperText = 'Select learning paths to automatically include all their courses',
  placeholder = 'Search and select learning paths...',
  disabled = false,
}: LearningPathMultiSelectProps<T>) {
  // Fetch published learning paths with React Query
  const {
    data: learningPathData,
    isLoading,
    error: fetchError,
  } = useQuery({
    queryKey: ['published-learning-paths-dropdown'],
    queryFn: () => learningPathApiService.getPublishedLearningPaths(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  // Convert learning paths to select options
  const learningPathOptions: SelectOption[] = learningPathData?.map((path: LearningPath) => ({
    label: path.title,
    value: path._id,
  })) || [];

  return (
    <div className="w-full">
      {label && (
        <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
          <div className="flex items-center gap-2">
            <BookMarked className="w-4 h-4 text-gray-500" />
            <span>{label}</span>
            {required && <span className="text-red-500 ml-1">*</span>}
          </div>
        </label>
      )}

      <div className="relative">
        <Select
          name={name}
          control={control}
          options={learningPathOptions}
          isMulti
          isSearchable
          placeholder={placeholder}
          isDisabled={disabled || isLoading}
          closeMenuOnSelect={false}
          hideSelectedOptions={true}
        />

        {isLoading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
            <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
          </div>
        )}
      </div>

      {error && (
        <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
          <span>{error}</span>
        </p>
      )}

      {helperText && !error && (
        <p className="text-gray-500 text-xs mt-1">{helperText}</p>
      )}

      {fetchError && (
        <p className="text-orange-600 text-xs mt-1">
          Warning: Failed to load learning paths. Please refresh the page.
        </p>
      )}
    </div>
  );
}
